import React, { useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import Button from "../../componentLibrary/Button";
import SearchResults from "./components/SearchResults";

const SearchSider = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentSearches, setRecentSearches] = useState<string[]>(
    JSON.parse(localStorage.getItem("recentSearches")) || [],
  );

  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);

  const closeSidebar = () => {
    setIsCollapsed(true);
    setSearchTerm("");
  };

  const handleClickOutside = (event) => {
    if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
      closeSidebar();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const searchVal = searchInputRef.current.value;
      if (searchVal.trim()) {
        updateRecentSearches(searchVal);
        setSearchTerm(searchVal);
        searchInputRef.current.value = "";
      }
    }
  };

  const updateRecentSearches = (term) => {
    const updatedSearches = [
      term,
      ...recentSearches.filter((item) => item !== term),
    ].slice(0, 5);
    setRecentSearches(updatedSearches);
    localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
  };

  const clearSearchHistory = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentSearches");
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <MagnifyingGlassIcon
        className="h-6 w-6 text-gray-700"
        onClick={() => setIsCollapsed(false)}
      />
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 h-full transition-all duration-300 ${
          isCollapsed ? "w-0" : !searchTerm ? "w-64 p-4" : "w-80 p-4"
        } bg-blue-50 overflow-hidden shadow-lg z-[9999]`}
      >
        <div
          className={`transition-opacity duration-200 space-y-8 ${
            isCollapsed ? "opacity-0" : "opacity-100"
          }`}
        >
          {searchTerm ? (
            <SearchResults
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setIsCollapsed={setIsCollapsed}
            />
          ) : (
            <>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search"
                  ref={searchInputRef}
                  onKeyDown={handleKeyDown}
                  className="w-full py-2 pl-10 pr-4 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mt-4">
                <h2 className="text-lg font-semibold">Recently searched</h2>
                <hr className="my-2" />
                <ul className="space-y-2">
                  {recentSearches.map((search, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <span>{search}</span>
                      <ArrowRightIcon
                        className="h-5 w-5 text-gray-900 cursor-pointer"
                        onClick={() => setSearchTerm(search)}
                      />
                    </li>
                  ))}
                </ul>
                {recentSearches.length > 0 && (
                  <Button
                    onClick={clearSearchHistory}
                    className="text-sm text-gray-900 ml-auto"
                    variant="alternative"
                  >
                    Clear all
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchSider;
